/**
 * ============================================================================
 * ZiniZ / ZiniPlaybook — Brand Tokens (SINGLE SOURCE OF TRUTH)
 * ============================================================================
 *
 * This file is the canonical brand-token registry for every ZiniZ product
 * (ZiniHub admin, ZiniHub customer, ZiniZ Super Admin, ZiBuzz, BrandBook).
 *
 * ----------------------------------------------------------------------------
 * AGENT RULES  (Claude / Cursor / Codex / Antigravity — read before editing)
 * ----------------------------------------------------------------------------
 *   ✅ DO   import { T, useTheme } from "<relative>/tokens";
 *   ✅ DO   reference colors as T.brand, T.cyan, T.amber … (never hex literals)
 *   ✅ DO   call `const th = useTheme(dark)` at the top of every component and
 *           use th.bg / th.surface / th.text … for theme-aware surfaces
 *   ✅ DO   use T.fast / T.mid / T.slow with T.ease (or T.spring) for motion
 *   ✅ DO   use T.rsm / T.rmd / T.rlg / T.rxl for border radii
 *
 *   ❌ DON'T copy this `T` object into a new file — always import it
 *   ❌ DON'T hardcode hex colors (#5e4ce2, #ef4444, …) in components
 *   ❌ DON'T add product-specific tokens here; this file is shared across ALL apps
 *   ❌ DON'T rename existing keys — many files depend on them
 *
 * ----------------------------------------------------------------------------
 * MINIMAL USAGE
 * ----------------------------------------------------------------------------
 *   import { T, useTheme } from "../../tokens";
 *
 *   export default function MyScreen({ dark }) {
 *     const th = useTheme(dark);
 *     return (
 *       <div style={{ background: th.bg, color: th.text, padding: 24 }}>
 *         <button style={{ background: T.brand, color: "#fff", borderRadius: T.rmd }}>
 *           Primary
 *         </button>
 *       </div>
 *     );
 *   }
 *
 * ============================================================================
 */

/**
 * Brand token registry.
 *
 * Groups:
 *   • Brand           — primary identity (purple) used by every app
 *   • Accents         — categorical / status hues (cyan, teal, amber, coral,
 *                       green, red, violet); each has a `*Soft` translucent
 *                       partner suitable for backgrounds
 *   • Light surfaces  — l-prefixed; used when `dark === false`
 *   • Dark surfaces   — d-prefixed; used when `dark === true`
 *   • Motion          — durations and easing curves
 *   • Radii           — corner radius scale
 *
 * @type {Readonly<Record<string, string>>}
 */
export const T = Object.freeze({
  // ── Brand ──────────────────────────────────────────────────────────────────
  brand:       "#5e4ce2",
  brandHover:  "#5046d4",
  brandSoft:   "rgba(94,76,226,0.12)",
  brandBorder: "rgba(94,76,226,0.28)",

  // ── Accent palette (each pair: solid + translucent soft) ──────────────────
  cyan:      "#06b6d4", cyanSoft:   "rgba(6,182,212,0.10)",
  teal:      "#14b8a6", tealSoft:   "rgba(20,184,166,0.10)",
  amber:     "#f59e0b", amberSoft:  "rgba(245,158,11,0.12)",
  coral:     "#f97316", coralSoft:  "rgba(249,115,22,0.10)",
  green:     "#22c55e", greenSoft:  "rgba(34,197,94,0.10)",
  red:       "#ef4444", redSoft:    "rgba(239,68,68,0.10)",
  violet:    "#7c3aed", violetSoft: "rgba(124,58,237,0.12)",

  // ── Light mode surfaces ────────────────────────────────────────────────────
  lCanvas:    "#f8fafc",
  lSurface:   "#ffffff",
  lMuted:     "#f1f5f9",
  lBorder:    "rgba(51,65,85,0.10)",
  lBorderMd:  "rgba(51,65,85,0.18)",
  lText:      "#0f172a",
  lTextSec:   "#475569",
  lTextMuted: "#94a3b8",

  // ── Dark mode surfaces ─────────────────────────────────────────────────────
  dCanvas:    "#0a0d12",
  dSurface:   "#111318",
  dElevated:  "#181c23",
  dMuted:     "#1e2330",
  dBorder:    "rgba(255,255,255,0.08)",
  dBorderMd:  "rgba(255,255,255,0.14)",
  dText:      "#f1f5f9",
  dTextSec:   "#b0bec5",
  dTextMuted: "#64748b",

  // ── Motion ─────────────────────────────────────────────────────────────────
  fast:   "150ms",
  mid:    "220ms",
  slow:   "320ms",
  ease:   "cubic-bezier(0.4,0,0.2,1)",
  spring: "cubic-bezier(0.34,1.56,0.64,1)",

  // ── Radii ──────────────────────────────────────────────────────────────────
  rsm: "6px",
  rmd: "10px",
  rlg: "16px",
  rxl: "24px",
});

/**
 * Returns a derived theme object for the current dark/light mode.
 *
 * Call once at the top of every component:
 *   const th = useTheme(dark);
 *
 * @param {boolean} dark - true → dark mode, false → light mode
 * @returns {{
 *   bg: string, surface: string, elevated: string, muted: string,
 *   border: string, borderMd: string,
 *   text: string, textSec: string, textMuted: string,
 *   sidebar: string, dark: boolean,
 *   tr: string, trSlow: string
 * }}
 */
export function useTheme(dark) {
  return {
    bg:        dark ? T.dCanvas    : T.lCanvas,
    surface:   dark ? T.dSurface   : T.lSurface,
    elevated:  dark ? T.dElevated  : T.lSurface,
    muted:     dark ? T.dMuted     : T.lMuted,
    border:    dark ? T.dBorder    : T.lBorder,
    borderMd:  dark ? T.dBorderMd  : T.lBorderMd,
    text:      dark ? T.dText      : T.lText,
    textSec:   dark ? T.dTextSec   : T.lTextSec,
    textMuted: dark ? T.dTextMuted : T.lTextMuted,
    sidebar:   dark ? "#0d1017"    : T.lSurface,
    dark,
    tr:     `all ${T.mid} ${T.ease}`,
    trSlow: `all ${T.slow} ${T.ease}`,
  };
}

/**
 * Accent lookup keyed by name. Useful for data-driven badges, chips, and
 * category pills where the color is decided at runtime.
 *
 *   ACCENTS.amber.solid  → "#f59e0b"
 *   ACCENTS.amber.soft   → "rgba(245,158,11,0.12)"
 *
 * @type {Readonly<Record<string, {solid: string, soft: string}>>}
 */
export const ACCENTS = Object.freeze({
  brand:  { solid: T.brand,  soft: T.brandSoft  },
  cyan:   { solid: T.cyan,   soft: T.cyanSoft   },
  teal:   { solid: T.teal,   soft: T.tealSoft   },
  amber:  { solid: T.amber,  soft: T.amberSoft  },
  coral:  { solid: T.coral,  soft: T.coralSoft  },
  green:  { solid: T.green,  soft: T.greenSoft  },
  red:    { solid: T.red,    soft: T.redSoft    },
  violet: { solid: T.violet, soft: T.violetSoft },
});
